import React, { useEffect, useState } from 'react';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { subAssignTool } from '../../../services/toolAssignmentService';
import { getUsersByRole } from '../../../services/userService';
import { useAuth } from '../../../contexts/AuthContext';

const SubAssignModal = ({ show, onHide, assignment }) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [holderId, setHolderId] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (show) { setHolderId(''); setNotes(''); setError(''); }
  }, [show]);

  const { data: techData } = useQuery({
    queryKey: ['technicians-list'],
    queryFn: () => getUsersByRole('technician'),
    enabled: show,
    staleTime: 5 * 60 * 1000
  });

  const mutation = useMutation({
    mutationFn: () => subAssignTool({
      parent_assignment_id: assignment?.id,
      holder_id: holderId,
      project_id: assignment?.project_id || null,
      notes: notes || null
    }),
    onSuccess: ({ error: err }) => {
      if (err) { setError(err.message); return; }
      toast.success('Herramienta sub-asignada.');
      queryClient.invalidateQueries({ queryKey: ['my-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['tool-detail'] });
      onHide();
    },
    onError: (e) => setError(e.message)
  });

  if (!assignment) return null;

  const technicians = (techData?.data || techData || []).filter(t => t.id !== user?.id);
  const toolName = assignment.tool?.name || 'Herramienta';
  const toolSerial = assignment.tool?.serial_number || '';

  return (
    <Modal show={show} onHide={onHide} centered fullscreen="sm-down">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-share me-2" />Sub-asignar a otro técnico
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Alert variant="info" className="py-2">
          <small>
            Le pasarás la herramienta a otro técnico. Cuando él la devuelva, te será devuelta a ti.
            Solo puedes hacer esto si tu asignación es primaria.
          </small>
        </Alert>
        <p className="mb-3">
          <strong>{toolName}</strong>{' '}
          {toolSerial && <span className="text-muted font-monospace">({toolSerial})</span>}
        </p>
        <Form.Group className="mb-3">
          <Form.Label>Técnico destino <span className="text-danger">*</span></Form.Label>
          <Form.Select
            value={holderId}
            onChange={e => setHolderId(e.target.value)}
            disabled={mutation.isPending}
          >
            <option value="">Seleccionar técnico...</option>
            {technicians.map(t => (
              <option key={t.id} value={t.id}>{t.full_name}</option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group>
          <Form.Label>Notas</Form.Label>
          <Form.Control
            as="textarea" rows={2}
            value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Para qué la necesita, cuándo te la devuelve..."
            disabled={mutation.isPending}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={mutation.isPending}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          disabled={!holderId || mutation.isPending}
          onClick={() => { setError(''); mutation.mutate(); }}
        >
          {mutation.isPending && <Spinner size="sm" animation="border" className="me-1" />}
          Confirmar sub-asignación
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SubAssignModal;
